import { OnDestroy, Directive, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ObjectID } from 'bson';
import { compact } from 'lodash-es';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subject, from, lastValueFrom, of } from 'rxjs';
import { map, tap, finalize, catchError } from 'rxjs/operators';

import {
  Company,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  Project,
  User,
  List,
  ListItem,
  StyleVariants,
} from '@site-mate/dashpivot-shared-library';

import { ListItemsService } from 'app/lists/list-items.service';
import { ListsService } from 'app/lists/lists.service';
import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Directive()
export abstract class AbstractListDetailsPageComponent implements OnDestroy {
  protected readonly onUnsubscribe = new Subject<void>();
  readonly subscribableUsers$ = new BehaviorSubject([] as User[]);
  readonly state = {
    isLoading: true,
    isUploading: false,
    selectedListId: undefined as string | undefined,
    isUpdating: false,
  };

  readonly listsHandler = new ListsHandler();
  company?: Company;
  project?: Project;
  parentId: string;
  triggerUploadListCSV = new EventEmitter<void>();
  folder: {
    hierarchy: Exclude<AppHierarchy, 'team'>;
    project?: Project;
    company?: Company;
  };

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly confirmService: ConfirmService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly listItemsService: ListItemsService,
    protected readonly listsService: ListsService,
    protected readonly router: Router,
    protected readonly segmentService: SegmentService,
    protected readonly toastrService: ToastrService,
    protected readonly userService: UserService,
    protected readonly eventsService: EventsService,
  ) {}

  get timezone() {
    const userTimezone = compact([this.userService.getCurrentUser().timezone, (moment as any).tz.guess()]);
    return userTimezone[0];
  }

  get isEditingListItem() {
    return this.listsHandler.selectedList?.items.some((listItem) => listItem.editing);
  }

  get canAddNewItem() {
    return !this.state.isLoading && !this.state.isUpdating && !this.isEditingListItem;
  }

  abstract getSubscribableUsers(): Observable<User[]>;
  abstract get canEditData(): boolean;
  abstract updateList(parentId: string, listId: string, list: List): Observable<List>;
  abstract deleteList(parentId: string, listId: string): Observable<void>;
  abstract uploadList(parentId: string, listId: string, formData: FormData): Observable<List>;

  ngOnDestroy() {
    this.onUnsubscribe.next();
    this.onUnsubscribe.complete();
  }

  getList(listId: string) {
    this.state.isLoading = true;
    return this.listsService.getList(listId).pipe(
      map((list) => ListsHandler.service.filterDeployedListItems(list, this.parentId)),
      tap((filteredList) => this.listsHandler.setLists([filteredList], this.timezone)),
      catchError(this.handleErrorAndContinue.bind(this)),
      finalize(() => {
        this.state.isLoading = false;
      }),
    );
  }

  onListDeployed(list: List) {
    const filteredList = ListsHandler.service.filterDeployedListItems(list, this.parentId);
    this.listsHandler.setLists([filteredList], this.timezone);
    this.listsHandler.setSelectedList(filteredList, this.timezone);
    this.listsHandler.addBlankEditableListItem();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onListDeployedViaCsv(_list: List, _fileList: FileList) {
    throw new Error('Not implemented');
  }

  onDeleteItems(listItemIds: string[]) {
    const removeItems$ = this.isListDeployed
      ? this.undeployItems(listItemIds)
      : this.removeItems(listItemIds);

    removeItems$.pipe(catchError(this.handleErrorAndContinue.bind(this))).subscribe((listItems) => {
      const filteredList = ListsHandler.service.filterDeployedListItems(listItems, this.parentId);
      this.listsHandler.setLists([filteredList], this.timezone);
      this.listsHandler.setSelectedList(filteredList, this.timezone);
    });
  }

  private removeItems(listItemIds: string[]) {
    const items = listItemIds.join(',');
    return this.listItemsService.removeListItems(
      this.selectedList._id,
      items,
      this.parentId,
      this.folder.hierarchy,
    );
  }

  private undeployItems(listItemIds: string[]) {
    return this.listItemsService.undeploy(this.selectedList._id, listItemIds, this.parentId);
  }

  get selectedList() {
    return this.listsHandler.selectedList;
  }

  onListSelected(list: List) {
    this.listsHandler.setSelectedList(list, this.timezone);
  }

  onListChanged($event: { list: List; insertNew: boolean; skipPersistence: boolean }) {
    const params = {
      insertNewListItem: $event.insertNew,
      hideMessages: true,
      skipTracking: true,
      skipPersistence: $event.skipPersistence,
    };

    return this.onListUpdated($event.list, params);
  }

  onListNameUpdated(newName: string) {
    return this.onListUpdated(
      { ...this.listsHandler.selectedList, name: newName },
      { successToastKey: 'listNameUpdated', errorToastKey: 'failedToUpdateListName' },
    );
  }

  onListItemStylesUpdated({ fullList }: { fullList: List }) {
    this.listsHandler.updateList(fullList.id, fullList, this.timezone);
  }

  async onListItemAdded({ position, name, list }: { position: number; name: string; list: List }) {
    const updatingThreshold = 400;
    this.state.isUpdating = list.items?.length > updatingThreshold;
    this.state.isLoading = true;

    const storableList = this.listsHandler.getStorableList(list);

    const { fullList } = await lastValueFrom(
      this.listItemsService.create(storableList._id, {
        position,
        name,
        parent: { hierarchy: this.folder.hierarchy, id: this.folder.project?._id || this.folder.company._id },
      }),
    );
    this.listsHandler.updateList(fullList.id, fullList, this.timezone);
    this.listsHandler.addBlankEditableListItem();

    this.state.isLoading = false;
    this.state.isUpdating = false;
  }

  async onListUpdated(sourceList: List, params = {} as Record<string, any>) {
    const updatingThreshold = 400;

    if (!params.skipTracking) {
      this.state.isLoading = true;
    }
    try {
      if (!params.skipPersistence) {
        this.state.isUpdating = sourceList.items?.length > updatingThreshold;
        const storableList = this.listsHandler.getStorableList(sourceList);
        const updatedList = await lastValueFrom(
          this.updateList(this.parentId, storableList.id, storableList),
        );
        this.listsHandler.updateList(updatedList.id, updatedList, this.timezone);
      } else {
        this.listsHandler.updateList(sourceList.id, sourceList, this.timezone);
      }
      if (!params.skipTracking) {
        const defaultSuccessToastKey = 'listDeployManagementSuccessful';
        this.toastrService.successByKey(params.successToastKey || defaultSuccessToastKey);
        this.eventsService.trackEvent(EventTypes.ListActionApplied, { Context: 'Name Edited' });
      }
      if (params.insertNewListItem) {
        this.listsHandler.addBlankEditableListItem();
      }
    } catch (error) {
      this.errorHandler.handle({ ...error, toastKey: params.errorToastKey });
    } finally {
      this.state.isUpdating = false;
    }

    if (!params.skipTracking) {
      this.state.isLoading = false;
    }
  }

  onListRemoved(list: List) {
    this.confirmService
      .confirmDelete('deleteList')
      .then(async () => {
        try {
          await this.deleteList(this.parentId, list.id).toPromise();
          this.toastrService.successByKey('listRemoved');
          this.listsHandler.deleteList(list.id, this.timezone);
          void EventNotifierService.notify(
            new DashpivotEvent(EventTypes.ListActionApplied, { Context: 'Deleted' }),
            this.segmentService,
          );
        } catch (e) {
          this.errorHandler.handle(e);
        }
        return true;
      })
      .catch(() => {});
  }

  onListDataUploaded(list: List, fileList: FileList) {
    const csvFile = fileList[0];
    const form = new FormData();
    form.append('file', csvFile);

    from(this.uploadList(this.parentId, list.id, form))
      .pipe(
        tap((updatedList) => this.listsHandler.updateList(updatedList.id, updatedList, this.timezone)),
        catchError(this.handleErrorAndContinue.bind(this)),
        finalize(() => {
          this.state.isUploading = false;
        }),
      )
      .subscribe();
  }

  onListAddNewItem() {
    this.listsHandler.addBlankEditableList();
  }

  onListAddStarted() {
    const id = new ObjectID().toHexString();
    const listItem = {
      id,
      _id: id,
      name: '',
      isNew: true,
      editing: true,
      properties: this.listsHandler.selectedList.properties.slice(),
      value: null,
      style: this.listsHandler.selectedList.defaultStyle || StyleVariants.None,
    } as ListItem;

    this.listsHandler.selectedList.items = this.listsHandler.selectedList.items.concat(listItem);
  }

  protected handleErrorAndContinue(error) {
    this.errorHandler.handle(error);
    return of(null);
  }

  get isListDeployed() {
    return this.listsHandler.selectedList?.deployedParents?.includes(this.parentId);
  }
}
